import { isSameDay, isBefore, startOfDay, addMinutes } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import {
  CALENDAR_SLOT_DURATION_MINUTES,
  TIME_PICKER_STEP_MINUTES,
} from "@/constants/calendar";

export function capEndToStartDay(start: Date, end: Date): Date {
  if (!isSameDay(end, start)) {
    const capped = new Date(start);
    capped.setHours(23, 45, 0, 0);
    return capped;
  }
  return end;
}

export function nextValidEndTime(
  start: Date,
  timezone?: string,
): Date | undefined {
  const bumped = addMinutes(start, TIME_PICKER_STEP_MINUTES);

  let capped: Date;
  if (timezone) {
    const zonedStart = toZonedTime(start, timezone);
    const zonedBumped = toZonedTime(bumped, timezone);
    if (isSameDay(zonedBumped, zonedStart)) {
      capped = bumped;
    } else {
      zonedStart.setHours(23, 45, 0, 0);
      capped = fromZonedTime(zonedStart, timezone);
    }
  } else {
    capped = capEndToStartDay(start, bumped);
  }

  return capped > start ? capped : undefined;
}

export const hasTimeOverlap = (
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date,
): boolean => start1 < end2 && end1 > start2;

export function buildZonedDateTime(
  day: string,
  time: string,
  timezone: string,
): string {
  const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
  return fromZonedTime(`${day}T${timeWithSeconds}`, timezone).toISOString();
}

export function applyTime(date: Date, time: string, timezone?: string): Date {
  const [h, m] = time.split(":").map(Number);
  if (timezone) {
    const zoned = toZonedTime(date, timezone);
    zoned.setHours(h, m, 0, 0);
    return fromZonedTime(zoned, timezone);
  }
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
}

export function alignEndToStartDay(
  start: Date,
  endTimeSource: Date,
  timezone?: string,
): Date {
  if (timezone) {
    const zonedStart = toZonedTime(start, timezone);
    const zonedEnd = toZonedTime(endTimeSource, timezone);
    zonedStart.setHours(zonedEnd.getHours(), zonedEnd.getMinutes(), 0, 0);
    return fromZonedTime(zonedStart, timezone);
  }
  const d = new Date(start);
  d.setHours(endTimeSource.getHours(), endTimeSource.getMinutes(), 0, 0);
  return d;
}

export function isSlotInPast(date: Date, hasPreciseTime?: boolean): boolean {
  if (isBefore(startOfDay(date), startOfDay(new Date()))) return true;
  return (
    !!hasPreciseTime &&
    isBefore(addMinutes(date, CALENDAR_SLOT_DURATION_MINUTES), new Date())
  );
}

export function ensureEndAfterStart(
  start?: Date,
  end?: Date,
  timezone?: string,
): { start: Date | undefined; end: Date | undefined } {
  if (!start) return { start, end };

  const safeEnd = end
    ? alignEndToStartDay(start, end, timezone)
    : new Date(start);

  if (safeEnd <= start) {
    return { start, end: nextValidEndTime(start, timezone) ?? safeEnd };
  }

  return { start, end: safeEnd };
}
